import { useLayoutEffect, useRef } from "react";
import { fitVideoFrameHeight, type VideoGridFit } from "./videoGridFit";

/**
 * 맞춘 프레임 높이를 담는 CSS 변수. 격자에는 이 변수를 읽는 고정 문자열을 전달한다.
 *
 * px 값을 prop 으로 전달하지 않는 이유는 둘이다.
 * - c6 격자는 `maxHeight` 를 styled-components 로 보간해 값마다 클래스를 새로 만든다.
 * - prop 이 바뀌면 격자를 다시 그린다.
 * 변수 하나만 바꾸면 창 크기를 바꾸는 동안에도 React 는 아무것도 다시 그리지 않는다.
 */
export const VIDEO_GRID_FRAME_HEIGHT_VAR = "--video-grid-frame-height";

/** 격자 `maxHeight` 로 전달할 문자열. 변수를 쓰기 전(첫 측정 전)에는 하한 값을 쓴다. */
export function videoGridFrameHeight(fit: VideoGridFit): string {
  return `var(${VIDEO_GRID_FRAME_HEIGHT_VAR}, ${fit.floorRem}rem)`;
}

/**
 * 격자 상자(`element`)를 재서 모든 카메라가 들어가는 프레임 높이를 변수에 쓴다.
 *
 * 마운트·카메라 수·규칙이 바뀌면 그리기 전에 한 번 동기로 잰다. 그래서 첫 화면이 하한 크기로
 * 나타났다가 커지는 깜빡임이 없다. 그 뒤의 크기 변화는 ResizeObserver 가 알린다. 쓰기는 다음
 * 프레임으로 미뤄 한 번에 합친다. 콜백 안에서 쓰면 그 쓰기가 관찰 대상의 크기(분석 열 폭,
 * 스크롤 막대)를 다시 바꾼다. 그러면 브라우저가 `ResizeObserver loop` 경고를 낸다.
 *
 * `onFrameFit` 은 값이 바뀔 때만 호출한다. 격자 바깥이 같은 값을 따라야 하는 자리(분석 탭의
 * 영상 열 폭)가 쓴다.
 */
export function useVideoGridFit(
  element: HTMLElement | null,
  {
    fit,
    count,
    onFrameFit,
  }: {
    fit: VideoGridFit | undefined;
    count: number;
    onFrameFit?: (px: number) => void;
  },
): void {
  // 최신 콜백을 effect 재구독 없이 호출한다. 소비처가 인라인 함수를 전달해도 관찰을 다시 등록하지 않는다.
  const onFrameFitRef = useRef(onFrameFit);
  onFrameFitRef.current = onFrameFit;

  useLayoutEffect(() => {
    if (element === null || fit === undefined) return;
    // 상자가 바뀌면 새 상자에는 변수가 없다. 그래서 직전 값을 상자마다 따로 보관한다.
    let last: number | null = null;
    const apply = (): void => {
      const box = window.getComputedStyle(element);
      const width =
        element.clientWidth -
        parseFloat(box.paddingLeft || "0") -
        parseFloat(box.paddingRight || "0");
      const height =
        element.clientHeight -
        parseFloat(box.paddingTop || "0") -
        parseFloat(box.paddingBottom || "0");
      const remPx = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 16;
      const px = fitVideoFrameHeight({ width, height, count, remPx, fit });
      if (px === null || px === last) return;
      last = px;
      element.style.setProperty(VIDEO_GRID_FRAME_HEIGHT_VAR, `${px}px`);
      onFrameFitRef.current?.(px);
    };
    apply();
    if (typeof ResizeObserver === "undefined") return;
    let frame: number | null = null;
    const observer = new ResizeObserver(() => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        apply();
      });
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
    };
  }, [element, fit, count]);
}
